import React, { useState, useEffect, useCallback } from "react"
import { Row, Col, Card, CardBody, Spinner, Alert, Table, Badge } from "reactstrap"
import ReactApexChart from "react-apexcharts"
import MetaTags from "react-meta-tags"
import api from "API/Api"

const Dashboard = () => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [elapsed, setElapsed] = useState(0)

  const fetchStats = useCallback(async () => {
    try {
      setError(null)
      const t0 = Date.now()
      console.time("⏱️ Dashboard API Total")
      const res = await api.get("dashboard-stats")
      console.timeEnd("⏱️ Dashboard API Total")
      if (res.data.success) {
        setStats(res.data.data)
        const serverMs = res.data.data?._debug?.queryTimeMs
        if (serverMs) {
          console.log(`%c📊 Dashboard: Server query=${serverMs}ms, Network round-trip=${Date.now() - t0}ms`, 'color: #f59e0b; font-weight: bold')
        }
      } else {
        setError(res.data.message || "Failed to load dashboard data")
      }
    } catch (err) {
      const msg = err.message || err.response?.data?.message || "Failed to connect to server"
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // Show elapsed seconds while loading
  useEffect(() => {
    if (!loading) return
    const timer = setInterval(() => setElapsed(prev => prev + 1), 1000)
    return () => clearInterval(timer)
  }, [loading])

  if (loading) {
    return (
      <>
        <MetaTags><title>Dashboard | NextGen Dairy</title></MetaTags>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "60vh" }}>
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <p className="text-muted mt-3">Loading dashboard data... {elapsed > 0 && <strong>({elapsed}s)</strong>}</p>
          {elapsed > 10 && <small className="text-warning">This may take a moment on initial load</small>}
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <MetaTags><title>Dashboard | NextGen Dairy</title></MetaTags>
        <Alert color="danger" className="m-4">
          <h5 className="alert-heading">Dashboard Error</h5>
          <p>{error}</p>
          <button className="btn btn-outline-danger btn-sm" onClick={() => { setLoading(true); fetchStats() }}>
            Retry
          </button>
        </Alert>
      </>
    )
  }

  if (!stats) return null

  const { counts, financials, monthlyTrends, milkTypeDistribution, shiftDistribution, topProducers, recentPurchases } = stats

  // --- Mini Widget Cards ---
  const widgets = [
    { title: "Avg Rate/Ltr", icon: "fas fa-rupee-sign", value: `₹${financials.avgRatePerLtr}`, color: "primary" },
    { title: "Producers", icon: "fas fa-users", value: counts.producers.toLocaleString(), color: "success" },
    { title: "Collection Centers", icon: "fas fa-building", value: counts.collectionCenters.toLocaleString(), color: "info" },
    { title: "Total Purchases", icon: "fas fa-shopping-cart", value: counts.totalPurchases.toLocaleString(), color: "warning" },
  ]

  // --- Monthly Trend Chart ---
  const trendMonths = (monthlyTrends || []).map(t => t.month)
  const trendRevenue = (monthlyTrends || []).map(t => parseFloat(t.revenue))
  const trendLitres = (monthlyTrends || []).map(t => parseFloat(t.litres))

  const trendChartOptions = {
    chart: { toolbar: { show: false }, zoom: { enabled: false } },
    colors: ["#556ee6", "#34c38f"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    xaxis: { categories: trendMonths },
    yaxis: [
      { title: { text: "Revenue (₹)" }, labels: { formatter: v => `₹${(v || 0).toLocaleString()}` } },
      { opposite: true, title: { text: "Litres" }, labels: { formatter: v => `${(v || 0).toLocaleString()} L` } },
    ],
    tooltip: { shared: true },
    legend: { position: "top" },
    grid: { borderColor: "#f1f1f1" },
  }
  const trendChartSeries = [
    { name: "Revenue (₹)", data: trendRevenue },
    { name: "Litres", data: trendLitres, yAxisIndex: 1 },
  ]

  // --- Milk Type Donut ---
  const milkLabels = (milkTypeDistribution || []).map(m => m.label)
  const milkValues = (milkTypeDistribution || []).map(m => parseFloat(m.litres))
  const donutOptions = {
    labels: milkLabels.length > 0 ? milkLabels : ["No Data"],
    colors: ["#556ee6", "#f46a6a", "#34c38f"],
    legend: { position: "bottom" },
    responsive: [{ breakpoint: 480, options: { chart: { width: 250 } } }],
  }

  // --- Shift Distribution Bar ---
  const shiftLabels = (shiftDistribution || []).map(s => s.label)
  const shiftValues = (shiftDistribution || []).map(s => parseFloat(s.litres))
  const shiftChartOptions = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "50%", borderRadius: 4 } },
    colors: ["#f1b44c"],
    dataLabels: { enabled: true, formatter: v => `${v} L` },
    xaxis: { categories: shiftLabels.length > 0 ? shiftLabels : ["No Data"] },
    yaxis: { title: { text: "Litres" } },
    grid: { borderColor: "#f1f1f1" },
  }

  return (
    <>
      <MetaTags><title>Dashboard | NextGen Dairy</title></MetaTags>

      {/* --- Stat Cards --- */}
      <Row>
        {widgets.map((w, i) => (
          <Col xl={3} sm={6} key={i}>
            <Card className={`mini-stat bg-${w.color}`}>
              <CardBody className="mini-stat-img">
                <div className="mini-stat-icon"><i className={`float-end ${w.icon}`}></i></div>
                <div className="text-white">
                  <h6 className="text-uppercase mb-3 font-size-16 text-white">{w.title}</h6>
                  <h2 className="mb-4 text-white">{w.value}</h2>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* --- Summary Cards --- */}
      <Row>
        <Col md={3}>
          <Card>
            <CardBody className="text-center">
              <h6 className="text-muted mb-2">Total Revenue</h6>
              <h4 className="text-primary">₹{parseFloat(financials.totalRevenue).toLocaleString()}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <CardBody className="text-center">
              <h6 className="text-muted mb-2">Total Litres</h6>
              <h4 className="text-success">{parseFloat(financials.totalLitres).toLocaleString()} L</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <CardBody className="text-center">
              <h6 className="text-muted mb-2">Avg Fat %</h6>
              <h4 className="text-warning">{financials.avgFat}%</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <CardBody className="text-center">
              <h6 className="text-muted mb-2">Avg SNF %</h6>
              <h4 className="text-info">{financials.avgSnf}%</h4>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* --- Charts Row --- */}
      <Row>
        <Col xl={8}>
          <Card>
            <CardBody>
              <h4 className="card-title mb-4">Monthly Purchase Trends</h4>
              {trendMonths.length > 0 ? (
                <ReactApexChart options={trendChartOptions} series={trendChartSeries} type="line" height="320" />
              ) : (
                <p className="text-muted text-center py-5">No purchase data available yet</p>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col xl={4}>
          <Card>
            <CardBody>
              <h4 className="card-title mb-4">Milk Type (Cow vs Buffalo)</h4>
              {milkValues.length > 0 ? (
                <ReactApexChart options={donutOptions} series={milkValues.length > 0 ? milkValues : [1]} type="donut" height="280" />
              ) : (
                <p className="text-muted text-center py-5">No data</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* --- Shift + Top Producers --- */}
      <Row>
        <Col xl={4}>
          <Card>
            <CardBody>
              <h4 className="card-title mb-4">Shift Distribution</h4>
              {shiftValues.length > 0 ? (
                <ReactApexChart options={shiftChartOptions} series={[{ name: "Litres", data: shiftValues }]} type="bar" height="250" />
              ) : (
                <p className="text-muted text-center py-5">No data</p>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col xl={8}>
          <Card>
            <CardBody>
              <h4 className="card-title mb-4">Top 5 Producers by Volume</h4>
              {(topProducers || []).length > 0 ? (
                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Producer</th>
                        <th>Purchases</th>
                        <th>Total Litres</th>
                        <th>Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducers.map((p, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td><strong>{p.name || "Unknown"}</strong></td>
                          <td>{p.purchases}</td>
                          <td>{parseFloat(p.totalLitres).toLocaleString()} L</td>
                          <td>₹{parseFloat(p.totalAmount).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted text-center py-3">No producer data yet</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* --- Recent Purchases --- */}
      <Row>
        <Col>
          <Card>
            <CardBody>
              <h4 className="card-title mb-4">Recent Purchases</h4>
              {(recentPurchases || []).length > 0 ? (
                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Producer</th>
                        <th>Center</th>
                        <th>Shift</th>
                        <th>Type</th>
                        <th>Litres</th>
                        <th>Fat %</th>
                        <th>SNF %</th>
                        <th>Rate/Ltr</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentPurchases.map((p, i) => (
                        <tr key={i}>
                          <td>{p.purchaseDate || "—"}</td>
                          <td>{p.producerName || "Unknown"}</td>
                          <td>{p.centerName || "—"}</td>
                          <td><Badge color={p.shift === 1 ? "success" : "warning"}>{p.shiftLabel}</Badge></td>
                          <td><Badge color={p.milkType === 1 ? "info" : "primary"}>{p.milkTypeLabel}</Badge></td>
                          <td>{p.qtyLtrs} L</td>
                          <td>{p.purchaseFat}%</td>
                          <td>{p.purchaseSnf}%</td>
                          <td>₹{p.ratePerLtr}</td>
                          <td><strong>₹{parseFloat(p.totalAmount).toLocaleString()}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted text-center py-3">No purchase records yet</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default Dashboard